import './style/productVAT.scss';
import {AwesomeIcon, ChangeFunction, ErrorType} from '@dvrd/dvr-controls';
import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import BolProduct from '../../../../models/bolProductModel';
import {getVatItemsByCountry} from '../../../../util/countryUtil';
import {SplitVATValue, VATPercentage, VATType, VATValue} from '../../../../util/interfaces';
import {truncateProductName} from '../../../../util/productsUtil';
import {PortalSelectItem} from '../../../../util/types';
import {BluemateDialog, BluemateInput, BluemateVatCountryRow} from '../../../controls';
import PortalSelectController from '../../../controls/portalSelect/portalSelectController';

interface Props {
    onClose: VoidFunction;
    onSubmit: VoidFunction;
    onClickChange: (countryCode: string, value: VATValue) => VoidFunction;
    onChange: (countryCode: string) => (value: string) => void;
    onAddCountry: ChangeFunction<string>;
    onDeleteCountry: (countryCode: string) => VoidFunction;
    onChangeSplitPercentage: (countryCode: string, percentage: string) => ChangeFunction<string>;
    onChangeSplitValue: (countryCode: string, percentage: string) => (value: string) => void;
    onEditDefaults?: VoidFunction;
    product: BolProduct;
    loading: boolean;
    active: boolean;
    errors: Record<string, ErrorType>;
    availableCountries: Array<[string, string]>;
}

const euroOrnament = {element: <span className='euro-ornament'>&euro;</span>};

/** The amount is whatever the user typed until it is saved, so it reaches the
 *  field as text and only becomes a number at the request boundary. */
type SplitVATEntry = [string, string | number];

const percentages = [
    VATPercentage.BE_LOW,
    VATPercentage.NL_LOW,
    VATPercentage.BE_MID,
    VATPercentage.DE_HIGH,
    VATPercentage.HIGH
];

function getAvailableSplitPercentages(currentPercentage: number, usedPercentages: Array<string>): Array<number> {
    return [currentPercentage]
        .concat(percentages.filter((perc: number) => !usedPercentages.includes(perc.toString())))
        .toSorted((percA: number, percB: number) => percA - percB);
}

export default function ProductVAT(props: Props) {
    const {
        onClose,
        onSubmit,
        onClickChange,
        onChange,
        onAddCountry,
        onDeleteCountry,
        onChangeSplitPercentage,
        onChangeSplitValue,
        product,
        loading,
        active,
        errors,
        availableCountries,
        onEditDefaults
    } = props;
    const {t} = useTranslation();
    const countryItems: Array<PortalSelectItem> = useMemo(
        () => availableCountries.map((entry: [string, string]) => ({value: entry[0], label: entry[1]})),
        [availableCountries]
    );

    function onSelectValue(countryCode: string, value: VATValue) {
        onClickChange(countryCode, value)();
    }

    function getDialogTitle() {
        return t('settings.vat.setCustomRatesFor', 'Afwijkende BTW tarieven instellen voor {{name}}', {
            name: truncateProductName(product.actualTitle)
        });
    }

    function renderCountryRow(countryCode: string) {
        const value = product.vats.getCountryValue(countryCode);
        return (
            <BluemateVatCountryRow
                key={countryCode}
                countryCode={countryCode}
                value={value}
                options={getVatItemsByCountry(countryCode)}
                onSelect={onSelectValue}
                onChangeCustom={onChange(countryCode)}
                onDelete={onDeleteCountry(countryCode)}
                error={errors[countryCode]}
                disabled={loading}
            >
                {value?.vat_type === VATType.SPLIT && renderSplitVAT(countryCode, value as SplitVATValue)}
            </BluemateVatCountryRow>
        );
    }

    function renderSplitVAT(countryCode: string, vatValue: SplitVATValue | null) {
        const entries: Array<SplitVATEntry> = Object.entries(vatValue?.value ?? {'9': 0, '21': 0}).slice(0, 2);
        if (!entries.length) entries.push(['9', 0], ['21', 0]);
        return <div className='split-vat-container'>{entries.map(renderSplitVATEntry(countryCode))}</div>;
    }

    function renderSplitVATEntry(countryCode: string) {
        return function (entry: SplitVATEntry, index: number, allEntries: Array<SplitVATEntry>) {
            const available = getAvailableSplitPercentages(
                Number(entry[0]),
                allEntries.map(current => current[0])
            );
            const percentageItems: Array<PortalSelectItem> = available.map((percentage: number) => ({
                label: `${percentage}%`,
                value: percentage.toString()
            }));
            return (
                <div key={index} className='split-vat-entry'>
                    <PortalSelectController
                        onChange={(value: string | null) => onChangeSplitPercentage(countryCode, entry[0])(value ?? '')}
                        value={entry[0]}
                        items={percentageItems}
                        label={t('settings.vat.vatPercent', 'BTW percentage')}
                        placeholder={t('settings.vat.choose', 'Kiezen...')}
                        disabled={loading}
                        shape='rounded'
                        uniformFont
                        noShadow
                    />
                    <BluemateInput
                        onChange={onChangeSplitValue(countryCode, entry[0])}
                        value={entry[1]}
                        ornaments={euroOrnament}
                        label={t('settings.vat.vatAmountPerUnit', 'BTW bedrag per stuk')}
                        disabled={loading}
                        allowCharReg={/[0-9.,]/}
                        inputProps={{inputMode: 'decimal'}}
                        shape='rounded'
                        uniformFont
                        autoSelect
                    />
                </div>
            );
        };
    }

    function renderRates() {
        if (!product.vats.length)
            return (
                <div className='no-custom-rate'>
                    <label>
                        {t(
                            'settings.vat.noCustomRate',
                            'Geen afwijken BTW tarief ingesteld. Gebruikt standaard tarief.'
                        )}
                    </label>
                    {!!onEditDefaults && (
                        <div className='edit-defaults' onClick={onEditDefaults}>
                            <label>{t('settings.vat.editDefaults', 'Standaard BTW tarieven instellen')}</label>
                            <AwesomeIcon name='cog' className='default-vat-icon' />
                        </div>
                    )}
                </div>
            );
        return <div className='bluemate-vat-country-rows'>{product.vats.sortedCountryCodes.map(renderCountryRow)}</div>;
    }

    function renderAddCountry() {
        if (!availableCountries.length) return null;
        return (
            <div className='add-country'>
                <label>{t('settings.vat.addCustomFor', 'Afwijken BTW tarief toevoegen voor:')}</label>
                <PortalSelectController
                    onChange={(value: string | null) => onAddCountry(value ?? '')}
                    value=''
                    items={countryItems}
                    placeholder={t('settings.vat.chooseCountry', 'Kies een land')}
                    disabled={loading}
                    shape='rounded'
                    uniformFont
                    noShadow
                    itemsHeight='20rem'
                />
            </div>
        );
    }

    return (
        <BluemateDialog
            active={active}
            onClose={onClose}
            title={getDialogTitle()}
            subtitle={`EAN: ${product.ean}`}
            onConfirm={onSubmit}
            confirmLabel={t('settings.vat.save', 'Opslaan')}
            cancelLabel={t('settings.vat.cancel', 'Annuleren')}
            cancelVariant='tertiary'
            confirmDisabled={loading}
            loading={loading}
            className='product-vat-dialog'
            width='60rem'
        >
            {renderRates()}
            {renderAddCountry()}
        </BluemateDialog>
    );
}
